import React from 'react'
import { isEmpty, size } from 'lodash'
import pluralize from 'pluralize'
import { IconBrandGoogleDrive, IconBrandOffice } from '@tabler/icons-react'

import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { SharePointConnectionForm } from '@/views/Organizations/components/SharePointConnectionForm'
import { GoogleDriveConnectionForm } from '@/views/Organizations/components/GoogleDriveConnectionForm'
import {
  ORG_NEW_FILES_PAUSED_LABEL,
  orgNewFilesPaused,
} from '@/views/Organizations/components/OrgSyncPausedBanner'
import { undismissedNotebooks } from '@/views/Organizations/components/unindexedNotebooks.helpers'
import {
  fetchSharePointConnections,
  fetchSharePointSyncState,
  purgeSharePointDocuments,
} from '@/reducers/organizations/sharepointConnection.actions'
import {
  fetchGoogleDriveConnections,
  fetchGoogleDriveSyncState,
  purgeGoogleDriveDocuments,
} from '@/reducers/organizations/googleDriveConnection.actions'

export const SHAREPOINT_PROVIDER = 'sharepoint'
export const GOOGLE_DRIVE_PROVIDER = 'google_drive'

// An unnamed connection still has to be identifiable in the list, and the host
// is the only other operator-legible field on the row. Mirrors the server's own
// fallback note on `label`.
const sharePointConnectionName = (connection) =>
  isEmpty(connection.label) ? connection.tenant_hostname : connection.label

// QCER-1101 gave Drive connections the same optional operator-given `label`
// SharePoint has, because an org may now hold several and `workspace_domain`
// is both optional and often the same across one customer's estates. Falls
// back to the domain, then to a bare provider name.
const googleDriveConnectionName = (connection) =>
  isEmpty(connection.label) ? connection.workspace_domain || 'Google Drive' : connection.label

// Destructive confirmation for erasing one connection's ingested documents.
// Names the connection AND its tenant host: once an org holds several, "erase
// this connection's documents" is only unambiguous if the dialog says which
// tenant, and picking the wrong one is unrecoverable.
const SharePointEraseConfirmModal = ({ connection, siblingCount, purging, onConfirm, onClose }) => (
  <ConfirmModal
    show
    onClose={onClose}
    title="Erase this connection’s documents?"
    confirmLabel="Erase documents"
    busyLabel="Erasing…"
    busy={purging}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="sharepoint-erase-documents"
  >
    <p>
      This erases every document ingested by <strong>{sharePointConnectionName(connection)}</strong>{' '}
      (tenant <strong>{connection.tenant_hostname}</strong>) from this organization’s knowledge
      base.
    </p>
    <ul>
      <li>The connection itself stays in the list, paused, marked as erased.</li>
      {siblingCount > 0 && (
        <li>
          {siblingCount === 1
            ? 'The other SharePoint connection on this organization is untouched.'
            : `The other ${siblingCount} SharePoint connections on this organization are untouched.`}
        </li>
      )}
      <li>Re-activating it re-ingests everything from scratch on the next sync.</li>
      <li>Erasing also clears the connection’s blocked files.</li>
    </ul>
    <p>This cannot be undone.</p>
  </ConfirmModal>
)

// Destructive confirmation for erasing one connection's ingested documents.
// QCER-1101 made the server scope this purge to the drives THIS connection
// ingested whenever the org holds others, so the wording is now SharePoint's
// rather than the org-wide warning it needed while Drive was capped at one.
const GoogleDriveEraseConfirmModal = ({
  connection,
  siblingCount,
  purging,
  onConfirm,
  onClose,
}) => (
  <ConfirmModal
    show
    onClose={onClose}
    title="Erase this connection’s documents?"
    confirmLabel="Erase documents"
    busyLabel="Erasing…"
    busy={purging}
    destructive
    onConfirm={onConfirm}
    testIdPrefix="google-drive-erase-documents"
  >
    <p>
      This erases every document ingested by{' '}
      <strong>{googleDriveConnectionName(connection)}</strong> from this organization’s knowledge
      base.
    </p>
    <ul>
      <li>The connection itself stays in the list, paused, marked as erased.</li>
      {siblingCount > 0 && (
        <li>
          {siblingCount === 1
            ? 'The other Google Drive connection on this organization is untouched.'
            : `The other ${siblingCount} Google Drive connections on this organization are untouched.`}
        </li>
      )}
      <li>Re-activating it re-ingests everything from scratch on the next sync.</li>
      <li>Erasing also clears the connection’s blocked files.</li>
    </ul>
    <p>This cannot be undone.</p>
  </ConfirmModal>
)

/**
 * Everything the merged Manage Knowledge Base panel needs to know per source
 * provider, so adding a provider is one entry here rather than a new tab.
 * Follows the OAUTH_INSTALL registry precedent (IntegrationCredentialForm).
 */
export const KNOWLEDGE_BASE_PROVIDERS = [
  {
    key: SHAREPOINT_PROVIDER,
    label: 'SharePoint',
    icon: <IconBrandOffice aria-label="SharePoint" />,
    connectionName: sharePointConnectionName,
    identifier: (connection) => connection.tenant_hostname,
    fetchConnections: fetchSharePointConnections,
    fetchSyncState: fetchSharePointSyncState,
    purgeDocuments: purgeSharePointDocuments,
    suggestionCount: (connection, dismissedKeys) =>
      size(undismissedNotebooks(connection, dismissedKeys)),
    // Older agent-gpt sends no held_libraries; none is what a missing key meant there.
    alertLabel: (connection) => {
      if (orgNewFilesPaused(connection)) {
        return ORG_NEW_FILES_PAUSED_LABEL
      }
      return (connection.held_libraries ?? 0) > 0
        ? `${pluralize('library', connection.held_libraries, true)} too large to sync`
        : null
    },
    FormComponent: SharePointConnectionForm,
    EraseConfirmModal: SharePointEraseConfirmModal,
    addTestId: 'knowledge-base-add-sharepoint',
    // An org may hold one connection per Entra tenant (QCER-1026) — no cap the
    // panel could enforce; a duplicate tenant is the server's 409 to raise.
    addDisabledReason: () => null,
    supportsManageFiles: true,
  },
  {
    key: GOOGLE_DRIVE_PROVIDER,
    label: 'Google Drive',
    icon: <IconBrandGoogleDrive aria-label="Google Drive" />,
    connectionName: googleDriveConnectionName,
    // The domain beneath the label. Falls back to the name so ConnectionList's
    // dedupe drops the line when there is no distinct identifier to show.
    identifier: (connection) =>
      connection.workspace_domain || googleDriveConnectionName(connection),
    fetchConnections: fetchGoogleDriveConnections,
    fetchSyncState: fetchGoogleDriveSyncState,
    purgeDocuments: purgeGoogleDriveDocuments,
    suggestionCount: () => 0,
    alertLabel: (connection) => (orgNewFilesPaused(connection) ? ORG_NEW_FILES_PAUSED_LABEL : null),
    FormComponent: GoogleDriveConnectionForm,
    EraseConfirmModal: GoogleDriveEraseConfirmModal,
    addTestId: 'knowledge-base-add-google-drive',
    // QCER-1101 lifted the one-per-org cap. The only duplicate the server
    // still refuses is a Shared Drive another connection already allowlists —
    // which lives in the allowlist the operator has not typed yet, so it is
    // the server's 409 to raise, not a cap the panel can predict.
    addDisabledReason: () => null,
    supportsManageFiles: true,
  },
]

export const providerOf = (key) => KNOWLEDGE_BASE_PROVIDERS.find((provider) => provider.key === key)
